/**
 * APY Comparison Chart
 *
 * Canvas-based bar chart comparing APY/APR across different staking products
 */

import { useRef, useEffect, useState } from 'react';
import type { StakingPosition } from '../../lib/api/cryptocom-api';
import { Icons } from '../Icons';

interface APYComparisonChartProps {
  positions: StakingPosition[];
}

interface ProductAPY {
  type: string;
  label: string;
  apr: number;
  apy: number;
  count: number;
  totalStaked: number;
  color: string;
}

const STAKING_TYPE_LABELS: Record<string, string> = {
  'CRO_LOCKUP': 'CRO Lockup',
  'CRO_SOFT_LOCKUP': 'CRO Soft Lockup',
  'DEFI_STAKING': 'DeFi Staking',
  'EARN': 'Crypto Earn',
  'CARD_STAKE': 'Card Stake'
};

const STAKING_TYPE_COLORS: Record<string, string> = {
  'CRO_LOCKUP': '#8b5cf6',
  'CRO_SOFT_LOCKUP': '#a78bfa',
  'DEFI_STAKING': '#10b981',
  'EARN': '#3b82f6',
  'CARD_STAKE': '#f59e0b'
};

export function APYComparisonChart({ positions }: APYComparisonChartProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [showAPR, setShowAPR] = useState(false);
  const [sortBy, setSortBy] = useState<'apy' | 'staked'>('apy');

  // Aggregate positions by type
  const productAPYs = (): ProductAPY[] => {
    const aggregated = new Map<string, ProductAPY>();

    positions.forEach(pos => {
      if (!aggregated.has(pos.type)) {
        aggregated.set(pos.type, {
          type: pos.type,
          label: STAKING_TYPE_LABELS[pos.type] || pos.type,
          apr: pos.apr,
          apy: pos.apy,
          count: 0,
          totalStaked: 0,
          color: STAKING_TYPE_COLORS[pos.type] || '#6b7280'
        });
      }

      const product = aggregated.get(pos.type)!;
      product.count += 1;
      product.totalStaked += pos.amount;
      // Average APY/APR
      product.apy = (product.apy * (product.count - 1) + pos.apy) / product.count;
      product.apr = (product.apr * (product.count - 1) + pos.apr) / product.count;
    });

    const products = Array.from(aggregated.values());

    // Sort by selected criteria
    if (sortBy === 'apy') {
      return products.sort((a, b) => b.apy - a.apy);
    } else {
      return products.sort((a, b) => b.totalStaked - a.totalStaked);
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * window.devicePixelRatio;
    canvas.height = rect.height * window.devicePixelRatio;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);

    const width = rect.width;
    const height = rect.height;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    const products = productAPYs();

    if (products.length === 0) {
      ctx.fillStyle = '#888';
      ctx.font = '14px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Aucun produit de staking disponible', width / 2, height / 2);
      return;
    }

    // Chart margins
    const margin = { top: 20, right: 20, bottom: 60, left: 80 };
    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    const maxRate = Math.max(...products.map(p => showAPR ? p.apr : p.apy));
    const barWidth = chartWidth / products.length;
    const barPadding = barWidth * 0.2;

    // Draw Y-axis
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(margin.left, margin.top);
    ctx.lineTo(margin.left, margin.top + chartHeight);
    ctx.stroke();

    // Draw horizontal grid lines and Y-axis labels
    const ySteps = 5;
    ctx.fillStyle = '#888';
    ctx.font = '11px sans-serif';
    ctx.textAlign = 'right';

    for (let i = 0; i <= ySteps; i++) {
      const y = margin.top + chartHeight - (chartHeight / ySteps) * i;
      const rate = (maxRate / ySteps) * i;

      // Grid line
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
      ctx.beginPath();
      ctx.moveTo(margin.left, y);
      ctx.lineTo(margin.left + chartWidth, y);
      ctx.stroke();

      // Y-axis label
      ctx.fillText(`${rate.toFixed(1)}%`, margin.left - 10, y + 4);
    }

    // Draw bars
    products.forEach((product, index) => {
      const rate = showAPR ? product.apr : product.apy;
      const barHeight = (rate / maxRate) * chartHeight;
      const x = margin.left + (index * barWidth) + barPadding / 2;
      const y = margin.top + chartHeight - barHeight;
      const actualBarWidth = barWidth - barPadding;

      // Draw bar with gradient
      const gradient = ctx.createLinearGradient(x, y, x, y + barHeight);
      gradient.addColorStop(0, product.color);
      gradient.addColorStop(1, product.color + '80');

      ctx.fillStyle = gradient;
      ctx.fillRect(x, y, actualBarWidth, barHeight);

      // Draw bar border
      ctx.strokeStyle = product.color;
      ctx.lineWidth = 2;
      ctx.strokeRect(x, y, actualBarWidth, barHeight);

      // Draw rate value on top of bar
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 13px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`${rate.toFixed(2)}%`, x + actualBarWidth / 2, y - 8);

      // Draw count indicator
      ctx.fillStyle = '#888';
      ctx.font = '10px sans-serif';
      ctx.fillText(`${product.count}x`, x + actualBarWidth / 2, y - 22);

      // Draw product label (rotated for better fit)
      ctx.save();
      ctx.translate(x + actualBarWidth / 2, margin.top + chartHeight + 15);
      ctx.rotate(-Math.PI / 6); // -30 degrees
      ctx.textAlign = 'right';
      ctx.fillStyle = '#ccc';
      ctx.font = '12px sans-serif';
      ctx.fillText(product.label, 0, 0);
      ctx.restore();
    });

    // Draw X-axis
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(margin.left, margin.top + chartHeight);
    ctx.lineTo(margin.left + chartWidth, margin.top + chartHeight);
    ctx.stroke();

  }, [positions, showAPR, sortBy]);

  const products = productAPYs();
  const averageAPY = products.length > 0
    ? products.reduce((sum, p) => sum + p.apy, 0) / products.length
    : 0;
  const totalStaked = products.reduce((sum, p) => sum + p.totalStaked, 0);
  const bestProduct = products.length > 0 ? products[0] : null;

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
        <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Icons.BarChart size={20} />
          Comparaison des Rendements
        </h3>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {/* APY/APR Toggle */}
          <button
            onClick={() => setShowAPR(!showAPR)}
            style={{
              padding: '6px 12px',
              background: showAPR ? 'var(--accent)' : 'transparent',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              color: showAPR ? 'white' : 'var(--text)',
              fontSize: '12px',
              fontWeight: 500,
              cursor: 'pointer'
            }}
          >
            {showAPR ? 'APR' : 'APY'}
          </button>

          {/* Sort Toggle */}
          <select
            aria-label="Sort tokens by"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'apy' | 'staked')}
            style={{
              padding: '6px 12px',
              background: 'var(--card)',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              color: 'var(--text)',
              fontSize: '12px',
              fontWeight: 500,
              cursor: 'pointer'
            }}
          >
            <option value="apy">Trier par APY</option>
            <option value="staked">Trier par montant</option>
          </select>
        </div>
      </div>

      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height: '300px',
          cursor: 'default'
        }}
      />

      {/* Summary Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '16px',
        marginTop: '16px',
        padding: '16px',
        background: 'rgba(255,255,255,0.03)',
        borderRadius: '8px'
      }}>
        <div>
          <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>APY Moyen</div>
          <div style={{ fontSize: '18px', fontWeight: 600, color: '#10b981' }}>
            {averageAPY.toFixed(2)}%
          </div>
        </div>
        <div>
          <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>Total Staké</div>
          <div style={{ fontSize: '18px', fontWeight: 600, color: '#fff' }}>
            ${totalStaked.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>Produits Actifs</div>
          <div style={{ fontSize: '18px', fontWeight: 600, color: '#fff' }}>
            {products.length}
          </div>
        </div>
        {bestProduct && (
          <div>
            <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>Meilleur Rendement</div>
            <div style={{ fontSize: '18px', fontWeight: 600, color: bestProduct.color }}>
              {bestProduct.label} ({bestProduct.apy.toFixed(2)}%)
            </div>
          </div>
        )}
      </div>

      {/* Legend */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '16px',
        marginTop: '16px',
        fontSize: '13px'
      }}>
        {products.map(product => (
          <div
            key={product.type}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <div
              style={{
                width: '16px',
                height: '16px',
                borderRadius: '3px',
                background: product.color
              }}
            />
            <span style={{ color: '#ccc' }}>
              {product.label}
            </span>
            <span style={{ color: '#888' }}>
              ({product.count} position{product.count > 1 ? 's' : ''})
            </span>
          </div>
        ))}
      </div>

      {/* Info box */}
      <div style={{
        marginTop: '16px',
        padding: '12px',
        background: 'rgba(59, 130, 246, 0.1)',
        borderRadius: '6px',
        borderLeft: '3px solid #3b82f6',
        fontSize: '12px',
        color: 'var(--muted)',
        lineHeight: '1.6'
      }}>
        <strong>💡 Note:</strong> {showAPR ? 'APR' : 'APY'} affiché.
        APY inclut les intérêts composés, APR est le taux simple.
        Les barres montrent le rendement moyen par type de produit.
      </div>
    </div>
  );
}
